// Delta application and reversal for undo/redo operations

import type { Room, Corridor } from "../types.ts";
import type { DungeonModel } from "../model/dungeon.ts";
import type {
  OperationDelta,
  SerializedRoom,
  SerializedCorridor,
} from "./tree.ts";

/**
 * Convert a Room to SerializedRoom for storage.
 */
export function serializeRoom(room: Room): SerializedRoom {
  return {
    id: room.id,
    hexes: Array.from(room.hexes),
    label: room.label,
    userDrawn: room.userDrawn,
    style: { ...room.style },
  };
}

/**
 * Convert a Corridor to SerializedCorridor for storage.
 */
export function serializeCorridor(corridor: Corridor): SerializedCorridor {
  return {
    id: corridor.id,
    hexes: [...corridor.hexes],
    connects: [...corridor.connects] as [string, string],
    width: corridor.width,
    userDrawn: corridor.userDrawn,
  };
}

/**
 * Restore a room directly into the dungeon model.
 * This bypasses the normal addRoom() to preserve the original ID.
 */
function restoreRoom(dungeon: DungeonModel, serialized: SerializedRoom): void {
  const room: Room = {
    id: serialized.id,
    hexes: new Set(serialized.hexes),
    label: serialized.label,
    userDrawn: serialized.userDrawn,
    style: { ...serialized.style },
  };

  dungeon.rooms.set(room.id, room);

  // Update hex-to-room mapping
  for (const hexKey of room.hexes) {
    (dungeon as unknown as { hexToRoom: Map<string, string> }).hexToRoom.set(
      hexKey,
      room.id
    );
  }
}

/**
 * Remove a room without removing connected corridors.
 * Used when we need precise control over which corridors are removed.
 */
function removeRoomOnly(dungeon: DungeonModel, roomId: string): void {
  const room = dungeon.rooms.get(roomId);
  if (!room) return;

  // Remove hex-to-room mappings
  for (const hexKey of room.hexes) {
    (dungeon as unknown as { hexToRoom: Map<string, string> }).hexToRoom.delete(
      hexKey
    );
  }

  dungeon.rooms.delete(roomId);
}

/**
 * Restore a corridor directly into the dungeon model.
 * This bypasses the normal addCorridor() to preserve the original ID.
 */
function restoreCorridor(
  dungeon: DungeonModel,
  serialized: SerializedCorridor
): void {
  const corridor: Corridor = {
    id: serialized.id,
    hexes: [...serialized.hexes],
    connects: [...serialized.connects] as [string, string],
    width: serialized.width,
    userDrawn: serialized.userDrawn,
  };

  dungeon.corridors.set(corridor.id, corridor);
}

/**
 * Apply a delta to move forward in history (redo direction).
 */
export function applyDelta(
  dungeon: DungeonModel,
  delta: OperationDelta
): void {
  switch (delta.type) {
    case "initial":
      // No-op for initial state
      break;

    case "addRoom":
      restoreRoom(dungeon, delta.room);
      break;

    case "removeRoom":
      // Remove the room and its corridors
      for (const corridor of delta.removedCorridors) {
        dungeon.corridors.delete(corridor.id);
      }
      removeRoomOnly(dungeon, delta.room.id);
      break;

    case "addCorridor":
      restoreCorridor(dungeon, delta.corridor);
      break;

    case "removeCorridor":
      dungeon.corridors.delete(delta.corridor.id);
      break;

    case "labelRoom": {
      const room = dungeon.rooms.get(delta.roomId);
      if (room) {
        room.label = delta.newLabel;
      }
      break;
    }

    case "moveRooms": {
      // Move all rooms
      for (const roomId of delta.roomIds) {
        dungeon.moveRoom(roomId, delta.deltaQ, delta.deltaR);
      }
      // Restore corridors to "after" state
      for (const corridor of delta.corridorsAfter) {
        dungeon.corridors.delete(corridor.id);
        restoreCorridor(dungeon, corridor);
      }
      break;
    }

    case "mergeRooms":
      // Remove old corridors
      for (const corridor of delta.removedCorridors) {
        dungeon.corridors.delete(corridor.id);
      }
      // Remove merged rooms
      for (const room of delta.removedRooms) {
        removeRoomOnly(dungeon, room.id);
      }
      // Add new merged room
      restoreRoom(dungeon, delta.newRoom);
      // Update corridor connections
      for (const update of delta.updatedCorridors) {
        const corridor = dungeon.corridors.get(update.corridorId);
        if (corridor) {
          corridor.connects = [...update.newConnects] as [string, string];
        }
      }
      break;

    case "carveRoom": {
      const room = dungeon.rooms.get(delta.originalRoomId);
      if (room) {
        // Remove carved hexes from original room
        for (const hexKey of delta.carvedRoom.hexes) {
          room.hexes.delete(hexKey);
          (
            dungeon as unknown as { hexToRoom: Map<string, string> }
          ).hexToRoom.delete(hexKey);
        }
        // Add the new carved room
        restoreRoom(dungeon, delta.carvedRoom);
      }
      break;
    }

    case "bisectRoom":
      // Remove original room
      removeRoomOnly(dungeon, delta.originalRoom.id);
      // Remove old corridors
      for (const corridor of delta.removedCorridors) {
        dungeon.corridors.delete(corridor.id);
      }
      // Add new rooms
      restoreRoom(dungeon, delta.newRooms[0]);
      restoreRoom(dungeon, delta.newRooms[1]);
      // Add new corridors
      for (const corridor of delta.addedCorridors) {
        restoreCorridor(dungeon, corridor);
      }
      break;

    case "extendRoom": {
      const room = dungeon.rooms.get(delta.roomId);
      if (room) {
        for (const hexKey of delta.addedHexes) {
          room.hexes.add(hexKey);
          (
            dungeon as unknown as { hexToRoom: Map<string, string> }
          ).hexToRoom.set(hexKey, delta.roomId);
        }
      }
      break;
    }

    case "scaleRooms": {
      // Apply room changes (expand/contract)
      for (const change of delta.roomChanges) {
        const room = dungeon.rooms.get(change.roomId);
        if (room) {
          // Add expanded hexes
          for (const hexKey of change.addedHexes) {
            room.hexes.add(hexKey);
            (dungeon as unknown as { hexToRoom: Map<string, string> }).hexToRoom.set(
              hexKey,
              change.roomId
            );
          }
          // Remove contracted hexes
          for (const hexKey of change.removedHexes) {
            room.hexes.delete(hexKey);
            (dungeon as unknown as { hexToRoom: Map<string, string> }).hexToRoom.delete(
              hexKey
            );
          }
        }
      }
      // Apply pushed room movements
      for (const pushed of delta.pushedRooms) {
        dungeon.moveRoom(pushed.roomId, pushed.deltaQ, pushed.deltaR);
      }
      // Restore corridors to "after" state
      for (const corridor of delta.corridorsAfter) {
        dungeon.corridors.delete(corridor.id);
        restoreCorridor(dungeon, corridor);
      }
      break;
    }

    case "deleteScribble":
      // Remove all scribbled items
      for (const room of delta.rooms) {
        dungeon.removeRoom(room.id);
      }
      for (const corridor of delta.corridors) {
        dungeon.corridors.delete(corridor.id);
      }
      break;
  }
}

/**
 * Reverse a delta to move backward in history (undo direction).
 */
export function reverseDelta(
  dungeon: DungeonModel,
  delta: OperationDelta
): void {
  switch (delta.type) {
    case "initial":
      // No-op for initial state
      break;

    case "addRoom":
      // Reverse of add is remove
      removeRoomOnly(dungeon, delta.room.id);
      break;

    case "removeRoom":
      // Reverse of remove is restore
      restoreRoom(dungeon, delta.room);
      for (const corridor of delta.removedCorridors) {
        restoreCorridor(dungeon, corridor);
      }
      break;

    case "addCorridor":
      // Reverse of add is remove
      dungeon.corridors.delete(delta.corridor.id);
      break;

    case "removeCorridor":
      // Reverse of remove is restore
      restoreCorridor(dungeon, delta.corridor);
      break;

    case "labelRoom": {
      const room = dungeon.rooms.get(delta.roomId);
      if (room) {
        room.label = delta.oldLabel;
      }
      break;
    }

    case "moveRooms": {
      // Reverse move by negating the delta for all rooms
      for (const roomId of delta.roomIds) {
        dungeon.moveRoom(roomId, -delta.deltaQ, -delta.deltaR);
      }
      // Restore corridors to "before" state
      for (const corridor of delta.corridorsBefore) {
        dungeon.corridors.delete(corridor.id);
        restoreCorridor(dungeon, corridor);
      }
      break;
    }

    case "mergeRooms":
      // Reverse of merge: remove new room, restore old rooms and corridors
      removeRoomOnly(dungeon, delta.newRoom.id);
      // Restore original rooms
      for (const room of delta.removedRooms) {
        restoreRoom(dungeon, room);
      }
      // Restore removed corridors
      for (const corridor of delta.removedCorridors) {
        restoreCorridor(dungeon, corridor);
      }
      // Revert corridor connection updates
      for (const update of delta.updatedCorridors) {
        const corridor = dungeon.corridors.get(update.corridorId);
        if (corridor) {
          corridor.connects = [...update.oldConnects] as [string, string];
        }
      }
      break;

    case "carveRoom": {
      // Remove the carved room
      removeRoomOnly(dungeon, delta.carvedRoom.id);
      // Restore hexes to original room
      const room = dungeon.rooms.get(delta.originalRoomId);
      if (room) {
        for (const hexKey of delta.originalHexes) {
          room.hexes.add(hexKey);
          (
            dungeon as unknown as { hexToRoom: Map<string, string> }
          ).hexToRoom.set(hexKey, delta.originalRoomId);
        }
      }
      break;
    }

    case "bisectRoom":
      // Remove new rooms
      removeRoomOnly(dungeon, delta.newRooms[0].id);
      removeRoomOnly(dungeon, delta.newRooms[1].id);
      // Remove added corridors
      for (const corridor of delta.addedCorridors) {
        dungeon.corridors.delete(corridor.id);
      }
      // Restore original room
      restoreRoom(dungeon, delta.originalRoom);
      // Restore original corridors
      for (const corridor of delta.removedCorridors) {
        restoreCorridor(dungeon, corridor);
      }
      break;

    case "extendRoom": {
      const room = dungeon.rooms.get(delta.roomId);
      if (room) {
        for (const hexKey of delta.addedHexes) {
          room.hexes.delete(hexKey);
          (
            dungeon as unknown as { hexToRoom: Map<string, string> }
          ).hexToRoom.delete(hexKey);
        }
      }
      break;
    }

    case "scaleRooms": {
      // Restore corridors to "before" state first
      for (const corridor of delta.corridorsBefore) {
        dungeon.corridors.delete(corridor.id);
        restoreCorridor(dungeon, corridor);
      }
      // Reverse pushed room movements (in reverse order)
      for (let i = delta.pushedRooms.length - 1; i >= 0; i--) {
        const pushed = delta.pushedRooms[i];
        // Restore room to its original position and state
        removeRoomOnly(dungeon, pushed.roomId);
        restoreRoom(dungeon, pushed.roomBefore);
      }
      // Reverse room changes (expand/contract)
      for (const change of delta.roomChanges) {
        const room = dungeon.rooms.get(change.roomId);
        if (room) {
          // Remove added hexes (reverse of expand)
          for (const hexKey of change.addedHexes) {
            room.hexes.delete(hexKey);
            (dungeon as unknown as { hexToRoom: Map<string, string> }).hexToRoom.delete(
              hexKey
            );
          }
          // Add back removed hexes (reverse of contract)
          for (const hexKey of change.removedHexes) {
            room.hexes.add(hexKey);
            (dungeon as unknown as { hexToRoom: Map<string, string> }).hexToRoom.set(
              hexKey,
              change.roomId
            );
          }
        }
      }
      break;
    }

    case "deleteScribble":
      // Reverse of delete is restore
      for (const room of delta.rooms) {
        restoreRoom(dungeon, room);
      }
      for (const corridor of delta.corridors) {
        restoreCorridor(dungeon, corridor);
      }
      break;
  }
}
